<template>
    <section>
        <UsersFilters @on-filter-change="loadAsyncData" />
        <BmvTable
            :columns="tableColumns"
            :data="users && users.data ? users.data : []"
            :loading="loading"
            :pagination="true"
            :total-results="users ? users.total : 0"
            :total-pages="users ? Math.ceil(users.total / users.per_page) : 0"
            :current-page="users ? users.current_page : 1"
            :per-page="users ? users.per_page : 20"
            :sort-field="config.sort"
            :sort-direction="config.sortDirection"
            @sort="onSort"
            @page-change="onPageChange"
        >
            <!-- ID -->
            <template #cell-id="{ row }">
                <span class="bmv-cell-id">#{{ row.id }}</span>
            </template>

            <!-- Name -->
            <template #cell-name="{ row }">
                <span style="font-weight: 600; color: #0f172a;">{{ row.name }}</span>
            </template>

            <!-- Role -->
            <template #cell-role="{ row }">
                <BmvBadge
                    v-if="roleName(row)"
                    :variant="roleVariant(row)"
                    size="sm"
                >
                    {{ roleLabel(row) }}
                </BmvBadge>
                <span v-else class="tu-role-empty">—</span>
            </template>

            <!-- Active toggle -->
            <template #cell-active="{ row }">
                <i
                    data-toggle="modal"
                    :data-target="'#modalDelete' + row.id"
                    class="fas cursor-pointer"
                    :class="row.active ? 'fa-toggle-on text-green-500' : 'fa-toggle-off text-gray-400'"
                    style="font-size: 1.3rem;"
                ></i>
                <ModalDeleteSave
                    :item="modalDelete(row.id, row.name, row.active)"
                />
            </template>

            <!-- Actions -->
            <template #cell-actions="{ row }">
                <div class="d-flex align-items-center justify-content-center" style="gap: 6px;">
                    <router-link
                        :to="`/company/user/${row.id}/profile`"
                        class="tu-btn tu-btn--view"
                        :title="$t('user_profile.title')"
                    >
                        <i class="fal fa-eye" style="color: #fff !important;"></i>
                    </router-link>
                    <router-link
                        :to="`/company/user/${row.id}/edit`"
                        class="tu-btn tu-btn--edit"
                        :title="$t('common.edit')"
                    >
                        <i class="fal fa-pen" style="color: #fff !important;"></i>
                    </router-link>
                    <button
                        v-if="row.id != userId && row.user_type != 777 && !isUnderImpersonation"
                        :disabled="getUserImpersonated && row.id == getUserImpersonated.id"
                        @click.prevent="impersonate(row.id)"
                        class="tu-btn tu-btn--impersonate"
                        :title="$t('impersonation.description')"
                    >
                        <i class="fal fa-user-secret" style="color: #fff !important;"></i>
                    </button>
                </div>
            </template>
        </BmvTable>

        <BmvLoader v-model="loading" full-page />
    </section>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import { mapActions, mapGetters, mapState } from 'vuex'
import BmvTable from '@/components/UI/BmvTable'
import BmvBadge from '@/components/UI/v2/BmvBadge.vue'
import BmvLoader from '@/components/UI/v2/BmvLoader.vue'
import UsersFilters from '@/components/Model/Company/UsersFilters'
import ModalDeleteSave from '@/components/UI/ModalDeleteSave'

const ROLE_VARIANTS = {
    'super-admin': 'danger',
    admin: 'primary',
    manager: 'info',
    agent: 'warning',
    viewer: 'neutral'
}

export default {
    name: 'TabUsers',
    components: {
        BmvTable,
        BmvBadge,
        BmvLoader,
        ModalDeleteSave,
        UsersFilters
    },
    data: function () {
        return {
            userId: '',
            loading: false,
            tableColumns: [
                { field: 'id', label: 'ID', sortable: true, width: '60px' },
                { field: 'name', label: this.$t('companies.name'), sortable: true },
                { field: 'job_title', label: this.$t('companies.function'), sortable: true },
                { field: 'email', label: this.$t('companies.email'), sortable: true },
                { field: 'role', label: this.$t('companies.user.role'), width: '120px' },
                { field: 'locale', label: this.$t('companies.language'), sortable: true, width: '80px', align: 'center' },
                { field: 'active', label: this.$t('companies.user.active.label'), sortable: true, width: '80px', align: 'center' },
                { field: 'actions', label: this.$t('companies.actions'), width: '160px', align: 'center' }
            ]
        }
    },
    mounted() {
        this.userId = JSON.parse(localStorage.getItem('user')).id
    },
    computed: {
        ...mapState('company', {
            users: (state) => state.users,
            config: (state) => state.usersConfig
        }),
        ...mapState('admin', {
            isUnderImpersonation: (state) => state.isUnderImpersonation
        }),
        ...mapGetters('admin', ['getUserImpersonated'])
    },
    methods: {
        ...mapActions('admin', ['impersonateUser']),
        roleName(row) {
            let role = row.role
            if (!role && Array.isArray(row.roles) && row.roles.length) {
                role = row.roles[0]
            }
            if (!role) return ''
            return typeof role === 'object' ? role.name : role
        },
        roleVariant(row) {
            return ROLE_VARIANTS[this.roleName(row)] || 'neutral'
        },
        roleLabel(row) {
            const name = this.roleName(row)
            if (!name) return ''
            const key = `companies.user.roles.${name}`
            const translated = this.$t(key)
            return translated !== key ? translated : name
        },
        impersonate(userId) {
            this.loading = true
            this.impersonateUser(userId)
                .then(() => {
                    window.location.href = '/'
                })
                .catch((e) => {
                    this.$store.dispatch('toast', {
                        type: 'error',
                        title: this.$t('error.' + (e.response && e.response.data ? e.response.data.error : 'unknown'))
                    })
                    this.loading = false
                })
        },
        modalDelete: function (id, name, status) {
            const deleteLink = localStorage.getItem('baseURL') + '/users/' + id
            return {
                id,
                name,
                deleteLink,
                object: "l'utilisateur",
                action: status == 1 ? 'Supprimer' : 'Sauvegarder'
            }
        },
        onSort({ field, direction }) {
            this.config.page = 1
            this.config.sort = field
            this.config.sortDirection = direction
            this.loadAsyncData()
        },
        onPageChange(page) {
            this.config.page = page
            this.loadAsyncData()
        },
        loadAsyncData() {
            this.$emit('get-data', this.config)
        }
    }
}
</script>

<style scoped>
.tu-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    text-decoration: none;
    transition: all 0.2s ease;
}

.tu-btn i {
    font-size: 0.78rem;
}

.tu-role-empty {
    color: var(--v2-text-4);
    font-size: 0.78rem;
}

.tu-btn--view {
    background: #0891B2;
}

.tu-btn--view:hover {
    background: #0e7490;
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(8, 145, 178, 0.3);
}

.tu-btn--edit {
    background: #f59e0b;
}

.tu-btn--edit:hover {
    background: #d97706;
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(245, 158, 11, 0.3);
}

.tu-btn--impersonate {
    background: #10b981;
}

.tu-btn--impersonate:hover {
    background: #059669;
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(16, 185, 129, 0.3);
}

.tu-btn--impersonate:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
}
</style>
